import { useEffect, useMemo, useState } from "react";
import { Button, Input, Select, Space, Switch, Table, Typography } from "antd";
import { StatusPill, type StatusTone } from "../components/StatusPill";
import { collection, onSnapshot, orderBy, query } from "firebase/firestore";
import { useLocation, useNavigate } from "react-router-dom";
import { db } from "../lib/firebase";
import { useAuth } from "../lib/auth";
import IntakeDetailsModal from "../components/IntakeDetailsModal";
import NewIntakeModal from "../components/NewIntakeModal";
import { TableSkeleton } from "../components/TableSkeleton";
import { EmptyState } from "../components/EmptyState";
import { useApexContext } from "../components/apex/ApexProvider";

interface IntakeAnswer {
  questionId: string;
  stepTitle?: string;
  question?: string;
  answer?: string;
}

interface IntakeItem {
  id: string;
  type?: string;
  status?: string;
  createdAt?: string;
  submittedAt?: string;
  intakeName?: string;
  intakeDescription?: string;
  submitterAgency?: string;
  submitterName?: string;
  submitterEmail?: string;
  summaryAnswers?: IntakeAnswer[];
  rejectionReason?: string;
  onHoldReason?: string;
  archived?: boolean;
  convertedProjectId?: string;
}

interface Vendor {
  id: string;
  profile?: string;
  capabilityTags?: string[];
  status?: string;
}

const statusOptions = [
  "Submitted",
  "Review Complete",
  "Initial Intake Call Complete",
  "Assessment Complete",
  "Advisory Board Review Complete",
  "Leadership Review Complete",
  "On Hold",
  "Rejected",
];

export default function Intakes() {
  const location = useLocation();
  const navigate = useNavigate();
  const { canWrite } = useAuth();
  const { setPageContext } = useApexContext();
  const [intakes, setIntakes] = useState<IntakeItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [vendors, setVendors] = useState<Vendor[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [newIntakeOpen, setNewIntakeOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [showArchived, setShowArchived] = useState(false);

  useEffect(() => {
    const intakesQuery = query(collection(db, "publicIntakes"), orderBy("createdAt", "desc"));
    const unsubscribe = onSnapshot(intakesQuery, (snapshot) => {
      const items = snapshot.docs
        .map((docItem) => ({
          ...(docItem.data() as IntakeItem),
          id: docItem.id,
        }))
        // OAIP-68: hide unsubmitted chat drafts. Backend persists these on every
        // turn so the AI can resume mid-conversation, but they're not real intakes
        // until the user clicks Submit Intake.
        .filter((i) => (i.status || "").toLowerCase() !== "in-progress");
      setIntakes(items);
      setLoading(false);
    });
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    const vendorsQuery = query(collection(db, "vendorsPublic"), orderBy("createdAt", "desc"));
    const unsubscribe = onSnapshot(vendorsQuery, (snapshot) => {
      const items = snapshot.docs.map((docItem) => ({
        ...(docItem.data() as Vendor),
        id: docItem.id,
      }));
      items.sort((a, b) => {
        const nameA = a.profile?.split("\n")[0] || "";
        const nameB = b.profile?.split("\n")[0] || "";
        return nameA.localeCompare(nameB);
      });
      setVendors(items);
    });
    return () => unsubscribe();
  }, []);

  // URL ?id= is the source of truth for the open detail modal — links from
  // briefs, chat, search results, and notifications can all deep-link, and the
  // back button works because closing the modal clears the param.
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const id = params.get("id");
    if (id && intakes.find((item) => item.id === id)) {
      setSelectedId(id);
      setIsModalOpen(true);
    } else if (!id && isModalOpen) {
      setIsModalOpen(false);
      setSelectedId(null);
    }
  }, [location.search, intakes]);

  const selectedIntake = useMemo(
    () => intakes.find((intake) => intake.id === selectedId) || null,
    [intakes, selectedId]
  );

  // Apex: open_entity broadcast routes through the URL so the back button
  // behaves the same way as a clicked link.
  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent).detail as
        | { type?: string; id?: string }
        | undefined;
      if (!detail || detail.type !== "intake" || !detail.id) return;
      navigate(`/intakes?id=${encodeURIComponent(detail.id)}`);
    };
    window.addEventListener("apex:openEntity", handler as EventListener);
    return () => window.removeEventListener("apex:openEntity", handler as EventListener);
  }, [navigate]);

  // Apex: page-context filters.
  useEffect(() => {
    setPageContext({
      filters: {
        searchQuery,
        status: filterStatus,
        showArchived,
      },
    });
  }, [searchQuery, filterStatus, showArchived, setPageContext]);

  // Apex: openEntity sync.
  useEffect(() => {
    if (!isModalOpen || !selectedIntake) {
      setPageContext({ openEntity: undefined });
      return;
    }
    setPageContext({
      openEntity: {
        type: "intake",
        id: selectedIntake.id,
        name: selectedIntake.intakeName || selectedIntake.submitterAgency || selectedIntake.id,
        status: selectedIntake.status,
      },
    });
    return () => setPageContext({ openEntity: undefined });
  }, [isModalOpen, selectedIntake, setPageContext]);

  // OAIP-68: in-progress drafts are filtered upstream; this just defaults
  // missing-status rows to "Submitted" for back-compat with older intake docs.
  const normalizeStatus = (status?: string) => status || "Submitted";

  const filteredIntakes = useMemo(() => {
    return intakes.filter((intake) => {
      const status = normalizeStatus(intake.status);
      const name = (intake.intakeName || "").toLowerCase();
      const agency = (intake.submitterAgency || "").toLowerCase();
      const matchesSearch =
        name.includes(searchQuery.toLowerCase()) ||
        agency.includes(searchQuery.toLowerCase());
      const matchesStatus = filterStatus === "all" || status === filterStatus;
      const matchesArchived = showArchived || !intake.archived;
      return matchesSearch && matchesStatus && matchesArchived;
    });
  }, [intakes, searchQuery, filterStatus, showArchived]);

  const columns = [
    {
      title: "Intake name",
      dataIndex: "intakeName",
      render: (_: string, intake: IntakeItem) => intake.intakeName || "—",
    },
    {
      title: "Agency",
      dataIndex: "submitterAgency",
      render: (agency: string | undefined) => agency || "—",
    },
    {
      title: "Status",
      dataIndex: "status",
      render: (_: string, intake: IntakeItem) => {
        const status = normalizeStatus(intake.status);
        const tone: StatusTone =
          status === "Rejected"
            ? "danger"
            : status === "On Hold"
              ? "warning"
              : status === "Leadership Review Complete"
                ? "success"
                : status === "Submitted"
                  ? "neutral"
                  : "info";
        return <StatusPill label={status} tone={tone} inline />;
      },
    },
    {
      title: "Submitted",
      dataIndex: "createdAt",
      className: "apex-col-date",
      render: (_: string, intake: IntakeItem) => {
        const timestamp = intake.submittedAt || intake.createdAt;
        if (!timestamp) return "—";
        const d = new Date(timestamp);
        return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
      },
    },
  ];

  // Top-of-page metric strip — counts derive from currently loaded intakes.
  const submittedCount = intakes.filter(
    (i) => normalizeStatus(i.status) === "Submitted" && !i.archived
  ).length;
  const inReviewCount = intakes.filter((i) => {
    const s = normalizeStatus(i.status);
    return (
      s !== "Submitted" &&
      s !== "Rejected" &&
      s !== "On Hold" &&
      s !== "Leadership Review Complete" &&
      !i.archived
    );
  }).length;
  const closedCount = intakes.filter((i) => {
    const s = normalizeStatus(i.status);
    return s === "Rejected" || s === "On Hold" || s === "Leadership Review Complete";
  }).length;

  return (
    <div className="apex-page">
      <div className="apex-page-header">
        <div>
          <p className="apex-eyebrow">Intake queue</p>
          <h1 className="apex-page-title" style={{ marginTop: 6 }}>
            Intakes
          </h1>
          <p className="apex-page-subtitle">
            Review incoming intakes, track status, and convert them into projects.
          </p>
        </div>
        {canWrite && (
          <Button type="primary" onClick={() => setNewIntakeOpen(true)}>
            New intake
          </Button>
        )}
      </div>

      {/* Compact metric strip */}
      <section
        className="apex-surface"
        style={{
          display: "flex",
          alignItems: "center",
          gap: 32,
          padding: "20px 24px",
          flexWrap: "wrap",
        }}
      >
        <div className="apex-metric">
          <span className="apex-metric__label">Submitted</span>
          <span className="apex-metric__value">{submittedCount}</span>
          <span className="apex-metric__detail">awaiting first review</span>
        </div>
        <div className="apex-metric-divider" />
        <div className="apex-metric">
          <span className="apex-metric__label">In review</span>
          <span className="apex-metric__value">{inReviewCount}</span>
          <span className="apex-metric__detail">past first triage</span>
        </div>
        <div className="apex-metric-divider" />
        <div className="apex-metric">
          <span className="apex-metric__label">Closed</span>
          <span className="apex-metric__value">{closedCount}</span>
          <span className="apex-metric__detail">approved, held, or rejected</span>
        </div>
      </section>

      <div>
        <div className="apex-toolbar">
          <Input.Search
            placeholder="Search intake name or agency"
            value={searchQuery}
            onChange={(event) => setSearchQuery(event.target.value)}
            aria-label="Search intakes by name or agency"
            style={{ minWidth: 240, maxWidth: 320, flex: 1 }}
          />
          <Select
            value={filterStatus}
            onChange={setFilterStatus}
            aria-label="Filter intakes by status"
            options={[
              { label: "All statuses", value: "all" },
              ...statusOptions.map((status) => ({ label: status, value: status })),
            ]}
            style={{ width: 220 }}
          />
          <Space size={8}>
            <Switch
              checked={showArchived}
              onChange={setShowArchived}
              aria-label="Toggle archived intakes"
              size="small"
            />
            <Typography.Text style={{ fontSize: 13, color: "var(--apex-ink-muted)" }}>
              Show archived
            </Typography.Text>
          </Space>
          <span style={{ marginLeft: "auto", fontSize: 12, color: "var(--apex-ink-muted)" }}>
            {filteredIntakes.length} of {intakes.length}
          </span>
        </div>

        <div className="apex-table-wrap">
          {loading ? (
            <div style={{ padding: 16 }}>
              <TableSkeleton rows={5} columns={4} />
            </div>
          ) : filteredIntakes.length === 0 ? (
            <EmptyState
              description={
                searchQuery || filterStatus !== "all"
                  ? "No intakes match your filters"
                  : "No intakes yet. Submissions from /agency and /vendor will appear here."
              }
            />
          ) : (
            <Table
              dataSource={filteredIntakes}
              columns={columns}
              rowKey="id"
              pagination={{ pageSize: 10, showSizeChanger: false }}
              aria-label="Intake submissions list"
              caption="Public intake submissions"
              onRow={(record) => ({
                onClick: () => {
                  navigate(`/intakes?id=${encodeURIComponent(record.id)}`);
                },
                style: { cursor: "pointer" },
              })}
            />
          )}
        </div>
      </div>

      {selectedIntake && isModalOpen && (
        <IntakeDetailsModal
          intake={selectedIntake}
          vendors={vendors}
          onClose={() => navigate("/intakes")}
        />
      )}

      <NewIntakeModal
        open={newIntakeOpen}
        onClose={() => setNewIntakeOpen(false)}
        onCreated={(intakeId) => {
          setNewIntakeOpen(false);
          navigate(`/intakes?id=${encodeURIComponent(intakeId)}`);
        }}
      />
    </div>
  );
}
